
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Decimal from 'decimal.js';
import reportsApi from '../../../../api/report.tsx';
import { formatPaymentType, ReportView } from '../../../../common/enums.tsx';
import { formatDateForReport } from '../../../../common/timeFunctions.tsx';
import { ReportDataPoint } from '../../../../common/types.tsx';
import LoadingSpinner from '../../../../components/loadingSpinner/LoadingSpinner.tsx';
import useRequest from '../../../../hooks/useRequest.tsx';
import { formatCurrency } from '../../../../common/moneyFunctions.tsx';
import { padDataPoints, parseCustomDate } from './chartFunctions.tsx';
import ReportHeader from './ReportHeader.tsx';
import StyledTableContainer from './StyledTableContainer.tsx';
import TpgeStackedBarChart from './TpgeStackedBarChart.tsx';
import useReportHeader from './useReportHeader.tsx';

const groupByPaymentType = (data: ReportDataPoint[] = [], timePeriod: number) => {
  const stackedData = data.reduce<ReportDataPoint[]>((out, curr) => {
    let datapoint = out.find(f => f.timestamp === curr.timestamp);
    if (!datapoint) {
      datapoint = { timestamp: curr.timestamp }
      out.push(datapoint)
    }
    datapoint[formatPaymentType(+curr.paymentType)] = new Decimal(curr?.totalSales ?? 0).toFixed(2);

    return out
  }, [])
  return stackedData.map(m => ({ ...m, timestamp: formatDateForReport(parseCustomDate(m.timestamp), timePeriod) }));
}


export default function TotalSalesReport() {
  const { date, setDate, timePeriod, setTimePeriod, view, handleViewChange, comparisonPeriods, setComparisonPeriods, clear } = useReportHeader()
  const { data, loading: isLoading } = useRequest(() => reportsApi.totalSales(date, timePeriod, comparisonPeriods), [date, timePeriod, comparisonPeriods]);

  const grouped: ReportDataPoint[] = groupByPaymentType(data ?? [], timePeriod)
  const reportData = padDataPoints(grouped ?? [], timePeriod, date, comparisonPeriods)

  let barKeys: string[] = []

  for (const datapoint of grouped) {
    let dpTotal = new Decimal(0)
    const keys = Object.keys(datapoint).filter(f => f !== 'timestamp')
    for (const k of keys) {
      barKeys.push(k)
      dpTotal = dpTotal.plus(new Decimal(datapoint[k] || 0))
    }
    datapoint.total = dpTotal.toFixed(2)
  }

  barKeys = [...new Set(barKeys)].sort((a, b) => a.localeCompare(b))
  const totalSales = grouped.reduce((sum, item) => sum.plus(new Decimal(item.total ?? 0)), new Decimal(0));

  return (
    <>
      <ReportHeader
        date={date}
        setDate={setDate}
        timePeriod={timePeriod}
        setTimePeriod={setTimePeriod}
        comparisonPeriods={comparisonPeriods}
        setComparisonPeriods={setComparisonPeriods}
        view={view}
        handleViewChange={handleViewChange}
        clear={clear}
      />
      <LoadingSpinner isLoading={isLoading}>
        <>
          {view === ReportView.Chart ? (
            <TpgeStackedBarChart
              startDate={date}
              data={reportData}
              barKeys={barKeys}
              timePeriod={timePeriod}
            />
          ) : (
            <StyledTableContainer>
              <Table size="medium" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Date/Time</TableCell>
                    {
                      barKeys.map(key => (<TableCell key={key} sx={{ fontWeight: 'bold', textAlign: 'right' }}>{key}</TableCell>))
                    }
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {grouped.map((row) => (
                    <TableRow key={row.paymentType}>
                      <TableCell component="th" scope="row">
                        {row.timestamp}
                      </TableCell>
                      {
                        barKeys.map(key => (<TableCell key={key} sx={{ textAlign: 'right' }}>{formatCurrency(row[key] ?? 0, true)}</TableCell>))
                      }
                      <TableCell sx={{ textAlign: 'right' }}>{formatCurrency(row.total ?? 0, true)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={3} />
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{ fontWeight: 'bold', textAlign: 'right' }}
                    >
                      Total Sales
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>
                      {formatCurrency(totalSales, true)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </StyledTableContainer>
          )}
        </>
      </LoadingSpinner>
    </>
  );
}

